import { type SortPayload, useStore } from "ra-core";
import { useMemo } from "react";

/**
 * Namespaced under the accounts list's own store key, so a saved view travels
 * with the widget it belongs to.
 */
export const ACCOUNTS_SAVED_VIEWS_KEY = "dashboard.accounts.savedViews";

/** A named filter set of the accounts list, with the sort it was saved under. */
export type AccountsSavedView = {
  name: string;
  filter: Record<string, unknown>;
  sort?: SortPayload;
};

/**
 * A stable empty default: a fresh literal would hand back a new array on every
 * render, and every consumer would recompute along with it.
 */
const NO_SAVED_VIEWS: AccountsSavedView[] = [];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isSortPayload = (value: unknown): value is SortPayload =>
  isPlainObject(value) &&
  typeof value.field === "string" &&
  (value.order === "ASC" || value.order === "DESC");

/**
 * The store is localStorage, which the user can edit by hand, so nothing read
 * from it is trusted. A view missing its name is dropped; a view whose filter
 * or sort is unusable keeps its name and loses only the broken part, which is
 * the reading that costs the user the least.
 */
const toSavedView = (value: unknown): AccountsSavedView | null => {
  if (!isPlainObject(value)) return null;
  const { name, filter, sort } = value;
  if (typeof name !== "string" || name === "") return null;

  return {
    name,
    filter: isPlainObject(filter) ? filter : {},
    ...(isSortPayload(sort) ? { sort } : {}),
  };
};

const readSavedViews = (stored: unknown): AccountsSavedView[] =>
  Array.isArray(stored)
    ? stored
        .map(toSavedView)
        .filter((view): view is AccountsSavedView => view !== null)
    : [];

export type AccountsSavedViewsState = {
  views: AccountsSavedView[];
  /** Appends a view. Callers reject a duplicate name before getting here. */
  saveView: (view: AccountsSavedView) => void;
  deleteView: (name: string) => void;
};

/**
 * The named filter sets of the accounts list, persisted per browser.
 *
 * Persistence goes through ra-core's store, the same primitive the application
 * configuration uses, so a view survives a reload without the widget touching
 * localStorage itself.
 */
export const useAccountsSavedViews = (): AccountsSavedViewsState => {
  const [stored, setStored] = useStore<unknown>(
    ACCOUNTS_SAVED_VIEWS_KEY,
    NO_SAVED_VIEWS,
  );

  const views = useMemo(() => readSavedViews(stored), [stored]);

  return {
    views,
    saveView: (view) => setStored([...views, view]),
    deleteView: (name) => setStored(views.filter((view) => view.name !== name)),
  };
};
